import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Badge, Avatar, Button, Input, Alert, AlertDescription, Spinner } from '@klaro7/ui';
import { Permission, UserRole, UserStatus } from '@klaro7/shared-types';

import { useAdminUsers, useCreateAdminUser, useUpdateAdminUser, useDeleteAdminUser } from '@/features/users/hooks/useAdminUsers';
import type { AdminUser, AdminUserQueryParams } from '@/features/users/services/user-admin.service';
import { usePermissions } from '@/features/rbac/usePermissions';
import { extractErrorMessage } from '@/lib/api';
import { useDebouncedUrlSearch, useListUrlState } from '@/lib/listUrlState';

const roleLabels: Record<string, { label: string; variant: 'primary' | 'secondary' | 'warning' | 'info' }> = {
  super_admin: { label: 'Super Admin', variant: 'primary' },
  admin: { label: 'Admin', variant: 'primary' },
  professional: { label: 'Professionista', variant: 'secondary' },
  user: { label: 'Utente', variant: 'info' },
};

const statusLabels: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'default' }> = {
  active: { label: 'Attivo', variant: 'success' },
  pending: { label: 'In Attesa', variant: 'warning' },
  waiting_questionnaire: { label: 'Questionario', variant: 'warning' },
  suspended: { label: 'Sospeso', variant: 'error' },
  inactive: { label: 'Inattivo', variant: 'default' },
};

const euroFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
});

const emptyCreateForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  phone: '',
};

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const createUser = useCreateAdminUser();
  const [form, setForm] = useState(emptyCreateForm);
  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState<{ type: 'error' | 'success'; message: string } | null>(null);

  function update(field: keyof typeof emptyCreateForm, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAlert(null);

    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.password) {
      setAlert({ type: 'error', message: 'Compila tutti i campi obbligatori' });
      return;
    }

    if (!/[a-z]/.test(form.password) || !/[A-Z]/.test(form.password) || !/[0-9]/.test(form.password) || form.password.length < 8) {
      setAlert({ type: 'error', message: 'La password deve avere almeno 8 caratteri, una maiuscola, una minuscola e un numero' });
      return;
    }

    if (form.password !== form.confirmPassword) {
      setAlert({ type: 'error', message: 'Le password non coincidono' });
      return;
    }

    try {
      await createUser.mutateAsync({
        email: form.email.trim(),
        password: form.password,
        role: UserRole.USER,
        profile: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: form.phone.trim() || undefined,
        },
      });
      onClose();
    } catch (error) {
      setAlert({ type: 'error', message: extractErrorMessage(error) });
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-6 py-4">
          <h3 className="font-semibold text-neutral-900">Nuovo Utente</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            aria-label="Chiudi"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          {alert && (
            <Alert variant={alert.type}>
              <AlertDescription>{alert.message}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Nome *"
              value={form.firstName}
              onChange={(event) => update('firstName', event.target.value)}
              placeholder="Mario"
              autoComplete="given-name"
            />
            <Input
              label="Cognome *"
              value={form.lastName}
              onChange={(event) => update('lastName', event.target.value)}
              placeholder="Rossi"
              autoComplete="family-name"
            />
          </div>

          <Input
            label="Email *"
            type="email"
            value={form.email}
            onChange={(event) => update('email', event.target.value)}
            placeholder="utente@klaro7.com"
            autoComplete="email"
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Password *"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={(event) => update('password', event.target.value)}
              placeholder="KlaroUs3r00"
              autoComplete="new-password"
            />
            <Input
              label="Conferma password *"
              type={showPassword ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={(event) => update('confirmPassword', event.target.value)}
              placeholder="Ripeti password"
              autoComplete="new-password"
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-neutral-500">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(event) => setShowPassword(event.target.checked)}
              className="h-3.5 w-3.5 accent-brand-teal"
            />
            Mostra password
          </label>

          <Input
            label="Telefono"
            value={form.phone}
            onChange={(event) => update('phone', event.target.value)}
            placeholder="+39 333 1234567"
            autoComplete="tel"
          />

          <div className="flex justify-end gap-3 border-t border-neutral-100 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button type="submit" loading={createUser.isPending}>
              Crea Utente
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Inline row actions: quick suspend/reactivate (toggles status) and delete.
 * Both destructive paths confirm before firing; mutations invalidate the list.
 */
function UserRowActions({ user }: { user: AdminUser }) {
  const updateUser = useUpdateAdminUser();
  const deleteUser = useDeleteAdminUser();

  const isSuspended = user.status === 'suspended' || user.status === 'inactive';
  const busy = updateUser.isPending || deleteUser.isPending;

  const fullName = `${user.profile?.firstName ?? ''} ${user.profile?.lastName ?? ''}`.trim() || user.email;

  const handleToggle = () => {
    if (busy) return;
    const next = isSuspended ? UserStatus.ACTIVE : UserStatus.SUSPENDED;
    if (next === UserStatus.SUSPENDED) {
      const ok = window.confirm(
        `Sospendere ${fullName}?\n\nL'utente non potrà più accedere fino alla riattivazione.`
      );
      if (!ok) return;
    }
    updateUser.mutate({ id: user.id, payload: { status: next } });
  };

  const handleDelete = () => {
    if (busy) return;
    const ok = window.confirm(
      `Eliminare definitivamente ${fullName}?\n\nQuesta azione è irreversibile.`
    );
    if (!ok) return;
    deleteUser.mutate(user.id);
  };

  return (
    <div className="flex items-center justify-end gap-1.5">
      <Button variant="outline" size="sm" onClick={handleToggle} disabled={busy}>
        {isSuspended ? 'Riattiva' : 'Sospendi'}
      </Button>
      <Button variant="danger" size="sm" onClick={handleDelete} disabled={busy}>
        Elimina
      </Button>
    </div>
  );
}

// Colonne ordinabili lato server (campi reali del documento User).
const SORTABLE_FIELDS = ['createdAt', 'profile.firstName', 'email', 'role', 'status'] as const;
type SortField = (typeof SORTABLE_FIELDS)[number];

function SortableTh({
  field,
  label,
  sortBy,
  sortOrder,
  onSort,
}: {
  field: SortField;
  label: string;
  sortBy: SortField;
  sortOrder: 'asc' | 'desc';
  onSort: (field: SortField) => void;
}) {
  const active = sortBy === field;
  return (
    <th
      className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700"
      aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-brand-teal"
      >
        {label}
        <span className={active ? 'text-brand-teal' : 'text-neutral-300'} aria-hidden>
          {active ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

export function AdminUsersPage() {
  const location = useLocation();
  const { get, set, page } = useListUrlState();
  const debouncedSearch = get('q');
  const roleFilter = get('role');
  const statusFilter = get('status');
  const fraudFilter = get('fraud');
  const sortByParam = get('sortBy');
  const sortBy: SortField = (SORTABLE_FIELDS as readonly string[]).includes(sortByParam)
    ? (sortByParam as SortField)
    : 'createdAt';
  const sortOrder: 'asc' | 'desc' = get('sortOrder') === 'asc' ? 'asc' : 'desc';
  const [search, setSearch] = useDebouncedUrlSearch(debouncedSearch, (q) => set({ q }));
  const setPage = (p: number) => set({ page: p });
  const handleSort = (field: SortField) => {
    // Stesso campo → inverte; campo nuovo → testo in ordine alfabetico, date dalla più recente.
    const nextOrder = field === sortBy
      ? (sortOrder === 'asc' ? 'desc' : 'asc')
      : (field === 'createdAt' ? 'desc' : 'asc');
    const isDefault = field === 'createdAt' && nextOrder === 'desc';
    set({ sortBy: isDefault ? null : field, sortOrder: isDefault ? null : nextOrder });
  };
  const [showCreate, setShowCreate] = useState(false);
  const { can } = usePermissions();
  const canCreateUsers = can(Permission.USERS_CREATE);
  const canManageUsers = can(Permission.USERS_UPDATE) || can(Permission.USERS_MANAGE);

  const params: AdminUserQueryParams = {
    page,
    limit: 20,
    sortBy,
    sortOrder,
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
    ...(roleFilter ? { role: roleFilter } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(fraudFilter ? { fraud: fraudFilter as AdminUserQueryParams['fraud'] } : {}),
  };

  const { data, isLoading } = useAdminUsers(params);

  const users = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Utenti</h1>
          <p className="mt-1 text-sm text-neutral-600">Gestisci gli utenti registrati sulla piattaforma</p>
        </div>
        {canCreateUsers && (
          <Button onClick={() => setShowCreate(true)}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Nuovo Utente
          </Button>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-80">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Cerca nome, email, telefono"
            className="h-9 pr-9"
            leftIcon={(
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
            )}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
              aria-label="Cancella ricerca"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <select
          value={roleFilter}
          onChange={(e) => set({ role: e.target.value })}
          className="h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none"
        >
          <option value="">Tutti i ruoli</option>
          <option value="user">Utente</option>
          <option value="professional">Professionista</option>
          <option value="admin">Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => set({ status: e.target.value })}
          className="h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none"
        >
          <option value="">Tutti gli stati</option>
          <option value="active">Attivo</option>
          <option value="pending">In Attesa</option>
          <option value="waiting_questionnaire">Questionario</option>
          <option value="suspended">Sospeso</option>
        </select>
        <select
          value={fraudFilter}
          onChange={(e) => set({ fraud: e.target.value })}
          className="h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none"
        >
          <option value="">Segnalazioni: tutte</option>
          <option value="flagged">Da revisionare</option>
          <option value="cleared">Verificati OK</option>
          <option value="confirmed">Abusi confermati</option>
          <option value="any">Con storia antifrode</option>
        </select>
        {pagination && (
          <span className="text-sm text-neutral-400">
            {pagination.total} utent{pagination.total === 1 ? 'e' : 'i'}
          </span>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50">
                  <SortableTh field="profile.firstName" label="Utente" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableTh field="email" label="Email" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableTh field="role" label="Ruolo" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableTh field="status" label="Stato" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Credito disponibile</th>
                  <SortableTh field="createdAt" label="Registrato" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  {canManageUsers && (
                    <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Azioni</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {users.map((user) => {
                  const role = roleLabels[user.role] ?? { label: user.role, variant: 'info' as const };
                  const status = statusLabels[user.status] ?? { label: user.status, variant: 'default' as const };
                  const fullName = `${user.profile?.firstName ?? ''} ${user.profile?.lastName ?? ''}`.trim();

                  return (
                    <tr key={user.id} className="transition-colors hover:bg-neutral-50/50">
                      <td className="px-5 py-3.5">
                        <Link
                          to={`/dashboard/admin/users/${user.id}`}
                          state={{ from: location.pathname + location.search }}
                          className="flex items-center gap-3 group">
                          <Avatar fallback={fullName} size="sm" />
                          <span className="font-medium text-neutral-900 group-hover:text-brand-teal transition-colors">
                            {fullName || 'Utente'}
                          </span>
                          {user.fraudStatus === 'flagged' && (
                            <span title="Sospetto multi-account: da revisionare" className="text-red-600">
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                          {user.fraudStatus === 'cleared' && (
                            <span title="Segnalazione antifrode revisionata: verificato OK" className="text-neutral-300">
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
                              </svg>
                            </span>
                          )}
                          {user.fraudStatus === 'confirmed' && (
                            <span title="Abuso multi-account confermato" className="text-red-800">
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" />
                              </svg>
                            </span>
                          )}
                        </Link>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-neutral-600">{user.email}</td>
                      <td className="px-5 py-3.5">
                        <Badge variant={role.variant}>{role.label}</Badge>
                      </td>
                      <td className="px-5 py-3.5">
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        {user.wallet ? (
                          <span className="whitespace-nowrap text-sm font-semibold text-brand-teal">
                            {euroFormatter.format(user.wallet.totalBalance)}
                          </span>
                        ) : (
                          <span className="text-sm text-neutral-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-neutral-600">
                        <span className="whitespace-nowrap">
                          {new Date(user.createdAt).toLocaleDateString('it-IT', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                        <span className="ml-1.5 text-xs text-neutral-400">
                          {new Date(user.createdAt).toLocaleTimeString('it-IT', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </td>
                      {canManageUsers && (
                        <td className="px-5 py-3.5">
                          <UserRowActions user={user} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {users.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
                <svg className="h-6 w-6 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
              </div>
              <p className="mt-4 font-medium text-neutral-700">Nessun utente trovato</p>
              <p className="mt-1 text-sm text-neutral-600">
                {debouncedSearch || roleFilter || statusFilter || fraudFilter ? 'Nessun risultato per questi filtri' : 'Gli utenti registrati appariranno qui'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            disabled={!pagination.hasPrevPage}
            onClick={() => setPage(page - 1)}
            className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-30"
          >
            Precedente
          </button>
          <span className="px-4 text-sm text-neutral-400">
            {pagination.page} / {pagination.totalPages}
          </span>
          <button
            disabled={!pagination.hasNextPage}
            onClick={() => setPage(page + 1)}
            className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-30"
          >
            Successiva
          </button>
        </div>
      )}

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
